import { ModuleConfig } from '../../../common/container/module'
import { Container, IInit } from '../../../common/container/Container'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { STATUS_SERVICE_KEY } from '../../../container/app'
import {
  Evaluation,
  ImportResult,
  PatientProtocol,
  Protocol,
  ProtocolPathology,
  ProtocolStatus,
  Rule,
  RuleSet,
} from '../models/Protocol'

export interface IProtocolApi extends IInit {
  list(status?: ProtocolStatus): Observable<Protocol[]>
  getByID(id: string): Observable<Protocol | undefined>
  create(name: string, description: string): Observable<Protocol | undefined>
  update(id: string, name: string, description: string): Observable<Protocol | undefined>
  remove(id: string): Observable<boolean>

  transition(id: string, action: string): Observable<Protocol | undefined>

  createRuleSet(rs: Partial<RuleSet>): Observable<RuleSet | undefined>
  removeRuleSet(id: string): Observable<boolean>
  createRule(r: Partial<Rule>): Observable<Rule | undefined>
  updateRule(id: string, r: Partial<Rule>): Observable<Rule | undefined>
  removeRule(id: string): Observable<boolean>

  assignmentsByPatient(patientID: string): Observable<PatientProtocol[]>
  pathologies(protocolID: string): Observable<ProtocolPathology[]>
  setPathologies(protocolID: string, pathologyIDs: string[]): Observable<boolean>

  evaluations(patientID: string, limit?: number): Observable<Evaluation[]>
  evaluateNow(patientID: string): Observable<Evaluation[]>

  importExcel(file: File, name: string): Observable<ImportResult | undefined>
}

export class ProtocolApi implements IProtocolApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as ModuleConfig).moduleFullUrl
  }

  // typed catchError: without this the pipe degrades the Observable to unknown.
  private onError<T>(fallback: T): (o: Observable<T>) => Observable<T> {
    return catchError<T, Observable<T>>((err: Error) => {
      this._statusService.sendStatus({ variant: 'error', error: err })
      return of(fallback)
    })
  }

  list(status?: ProtocolStatus): Observable<Protocol[]> {
    const url = status ? `${this._url}?status=${status}` : this._url
    return this._httpClient.get<Protocol[]>({ url }).pipe(this.onError<Protocol[]>([]))
  }

  getByID(id: string): Observable<Protocol | undefined> {
    return this._httpClient
      .get<Protocol>({ url: `${this._url}/${id}` })
      .pipe(this.onError<Protocol | undefined>(undefined))
  }

  create(name: string, description: string): Observable<Protocol | undefined> {
    return this._httpClient.post<Protocol>({ url: this._url, body: { name, description } }).pipe(
      map((p: Protocol) => {
        this._statusService.sendStatus({ variant: 'success' })
        return p
      }),
      this.onError<Protocol | undefined>(undefined)
    )
  }

  update(id: string, name: string, description: string): Observable<Protocol | undefined> {
    return this._httpClient
      .put<Protocol>({ url: `${this._url}/${id}`, body: { name, description } })
      .pipe(
        map((p: Protocol) => {
          this._statusService.sendStatus({ variant: 'success' })
          return p
        }),
        this.onError<Protocol | undefined>(undefined)
      )
  }

  remove(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: `${this._url}/${id}` }).pipe(this.onError<boolean>(false))
  }

  // submit | approve | reject | retire | versions
  transition(id: string, action: string): Observable<Protocol | undefined> {
    return this._httpClient.post<Protocol>({ url: `${this._url}/${id}/${action}`, body: {} }).pipe(
      map((p: Protocol) => {
        this._statusService.sendStatus({ variant: 'success' })
        return p
      }),
      this.onError<Protocol | undefined>(undefined)
    )
  }

  createRuleSet(rs: Partial<RuleSet>): Observable<RuleSet | undefined> {
    return this._httpClient
      .post<RuleSet>({ url: `${this._url}/rule-sets`, body: rs })
      .pipe(this.onError<RuleSet | undefined>(undefined))
  }

  removeRuleSet(id: string): Observable<boolean> {
    return this._httpClient
      .delete({ url: `${this._url}/rule-sets/${id}` })
      .pipe(this.onError<boolean>(false))
  }

  createRule(r: Partial<Rule>): Observable<Rule | undefined> {
    return this._httpClient
      .post<Rule>({ url: `${this._url}/rules`, body: r })
      .pipe(this.onError<Rule | undefined>(undefined))
  }

  updateRule(id: string, r: Partial<Rule>): Observable<Rule | undefined> {
    return this._httpClient
      .put<Rule>({ url: `${this._url}/rules/${id}`, body: r })
      .pipe(this.onError<Rule | undefined>(undefined))
  }

  removeRule(id: string): Observable<boolean> {
    return this._httpClient
      .delete({ url: `${this._url}/rules/${id}` })
      .pipe(this.onError<boolean>(false))
  }

  assignmentsByPatient(patientID: string): Observable<PatientProtocol[]> {
    return this._httpClient
      .get<PatientProtocol[]>({ url: `${this._url}/assignments/patient/${patientID}` })
      .pipe(this.onError<PatientProtocol[]>([]))
  }

  pathologies(protocolID: string): Observable<ProtocolPathology[]> {
    return this._httpClient
      .get<ProtocolPathology[]>({ url: `${this._url}/${protocolID}/pathologies` })
      .pipe(this.onError<ProtocolPathology[]>([]))
  }

  setPathologies(protocolID: string, pathologyIDs: string[]): Observable<boolean> {
    return this._httpClient
      .put<boolean>({ url: `${this._url}/${protocolID}/pathologies`, body: { pathologyIDs } })
      .pipe(
        map(() => {
          this._statusService.sendStatus({ variant: 'success' })
          return true
        }),
        this.onError<boolean>(false)
      )
  }

  evaluations(patientID: string, limit = 50): Observable<Evaluation[]> {
    return this._httpClient
      .get<Evaluation[]>({ url: `${this._url}/evaluations/patient/${patientID}?limit=${limit}` })
      .pipe(this.onError<Evaluation[]>([]))
  }

  evaluateNow(patientID: string): Observable<Evaluation[]> {
    return this._httpClient
      .post<Evaluation[]>({ url: `${this._url}/evaluations/patient/${patientID}`, body: {} })
      .pipe(this.onError<Evaluation[]>([]))
  }

  // The import goes as multipart, so it does not go through the common HTTP client.
  importExcel(file: File, name: string): Observable<ImportResult | undefined> {
    const form = new FormData()
    form.append('file', file)
    form.append('name', name)

    return new Observable<ImportResult | undefined>((observer) => {
      fetch(`${this._url}/import/excel`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      })
        .then((r) => r.json())
        .then((r: ImportResult) => {
          observer.next(r)
          observer.complete()
        })
        .catch((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          observer.next(undefined)
          observer.complete()
        })
    })
  }
}
